import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

import "../../../../../assets/css/profile.page.css";
import "../../../../../assets/css/utility/input-field.css";
import "../../../../../assets/css/create-load-post.css";

import { ManageLoadPostContext } from "../..";
import AdditionalInfo from "./AdditionalInfo";
import Loader from "./Loader";
import {
  CREATE_LOAD_URL,
  SINGLE_LOAD_POST_UPDATE_URL,
} from "../../../../../utils/api-ends";
import useHttp from "../../../../../hooks/useHttp";
import LoaderImg from "../../../../../assets/media/loader.gif";
import Alert from "@mui/material/Alert";

const LoadsInformation = ({ isForUpdate }) => {
  const additionalInfoRef = useRef(null)
  const [loadPostState, loadPostContextDispatch] = useContext(
    ManageLoadPostContext
  );
  const loadPostInfo = loadPostState.loadPostInfo;
  const { isLoading, error, sendRequests: submitLoadPostData } = useHttp();
  const [nonFieldErrors, setNonFieldErrors] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [successMsgId, setSuccessMsgId] = useState("");
  const { loadPostId } = useParams();
  const navigate = useNavigate();

  const backToPreviousStep = () =>
    loadPostContextDispatch({ type: "CHANGE_STEP", payload: 1 });

  const validateStepTwo = () => {
    // Clear step 2 error messages
    loadPostContextDispatch({ type: "CLEAR_STEP_TWO_ERROR_MESSAGES" });

    const requiredFieldList = [
      "price_to_pay_carrier",
      "load_type",
      "delivery_date",
      "pickup_date",
      "additional_information",
      "payment_type",
    ];

    // Validate all fields
    let isValid = true;
    requiredFieldList.forEach((field) => {
      if (["", null, undefined].includes(loadPostInfo[field])) {
        isValid = false;

        // Set error messages
        loadPostContextDispatch({
          type: "MANAGE_ERROR_MESSAGES",
          payload: {
            name: field,
            value: ["This field is required"],
          },
        });
      }
    });
    return isValid;
  };

  const handleSubmit = (e) => {
    // Validate step 2
    if (!validateStepTwo()) {
      additionalInfoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return;
    }
    // Clear server non-field errors during submit
    setNonFieldErrors([]);
    setSuccessMsg("");
    loadPostContextDispatch({
      type: "CLEAR_STEP_TWO_ERROR_MESSAGES",
    });

    const response = (data) => {
      setSuccessMsg(data.message);
      setSuccessMsgId(data.data);
      window.scrollTo(0, 0);
    };

    let minimum_price_validation =
      loadPostState.minimum_price_to_pay_carrier <=
      Number(loadPostInfo.price_to_pay_carrier);
    if (!minimum_price_validation) {
      loadPostContextDispatch({
        type: "MANAGE_ERROR_MESSAGES",
        payload: {
          name: "price_to_pay_carrier",
          value: [
            `Minimum price ${loadPostState.minimum_price_to_pay_carrier} USD`,
          ],
        },
      });
      window.scrollTo(0, 0);
      return;
    }

    // Submit Load Feed data to server
    let submitUrl = CREATE_LOAD_URL;
    let method = "post";
    if (loadPostState.isForUpdate) {
      submitUrl = SINGLE_LOAD_POST_UPDATE_URL(loadPostId);
      method = "put";
    }
    submitLoadPostData(
      {
        url: submitUrl,
        method: method,
        data: {
          ...loadPostInfo,
          origin_contact_phone_number: `+1${loadPostInfo.origin_contact_phone_number}`,
          destination_contact_phone_number: `+1${loadPostInfo.destination_contact_phone_number}`
        },
      },
      response
    );
  };

  // Append field related error messages
  useEffect(() => {
    if (error && Object.keys(error)?.length !== 0) {
      loadPostContextDispatch({
        type: "APPEND_ERROR_MESSAGES",
        payload: error,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Append non-field related error messages
  useEffect(() => {
    if (error?.non_field_error?.length > 0) {
      setNonFieldErrors(error.non_field_error);
      window.scrollTo(0, 0);
    }
    if (error?.__all__?.length > 0) {
      setNonFieldErrors(error.__all__);
      window.scrollTo(0, 0);
    }
    if (error?.general?.length > 0) {
      setNonFieldErrors(error.general);
      window.scrollTo(0, 0);
    }
    if (error?.message?.length > 0) {
      setNonFieldErrors([error.message]);
      window.scrollTo(0, 0);
    }
  }, [error]);

  // If there is error in step 1 go back to step 1 from step 2
  useEffect(() => {
    const step1Fields = [
      "origin_contact_name",
      "origin_contact_phone_number",
      "origin_address",
      "origin_city",
      "origin_state",
      "origin_postal_code",
      "origin_latitude",
      "origin_longitude",
      "is_origin_auction",
      "destination_contact_name",
      "destination_contact_phone_number",
      "destination_address",
      "destination_city",
      "destination_state",
      "destination_postal_code",
      "destination_latitude",
      "destination_longitude",
      "is_destination_port",
    ];

    Object.keys(loadPostState.loadPostInfoErrors).forEach(function (key) {
      if (
        step1Fields.includes(key) &&
        loadPostState.loadPostInfoErrors[key]?.length > 0
      ) {
        backToPreviousStep();
        window.scrollTo(0, 0);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, loadPostState]);

  return (
    <>
      {successMsg ? (
        <section id="load-post-success-message-area">
          <div className="container theme-section">
            <div>
              <Alert severity="success" className="load-post-success-message">
                {successMsg}
              </Alert>

              <div className="multiple-btn offer-table-action-btn mt-5">
                <Link
                  className="view-load-button"
                  to={`/load-post/details/${loadPostId || successMsgId}`}>
                  <button> Load Post Details </button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* for ui purpose the component set at reverse position */}
          <div id="load-post-empty-content"></div>

          {!["", null, undefined].includes(loadPostInfo.load_type) && (
            <Loader />
          )}

          <AdditionalInfo nonFieldErrors={nonFieldErrors} additionalInfoRef={additionalInfoRef} />

          <section id="load-post-btn">
            <div className="container">
              <div className="multiple-btn">
                {isForUpdate === true &&
                loadPostState?.loadPostData?.can_update === true ? (
                  <button onClick={handleSubmit}>
                    {" "}
                    Update{" "}
                    {isLoading && (
                      <img style={{ width: "20px" }} src={LoaderImg} alt="" />
                    )}
                  </button>
                ) : isForUpdate === false ? (
                  <button onClick={handleSubmit}>
                    {" "}
                    Submit{" "}
                    {isLoading && (
                      <img style={{ width: "20px" }} src={LoaderImg} alt="" />
                    )}
                  </button>
                ) : null}
                <button onClick={backToPreviousStep}>Back</button>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
};

export default React.memo(LoadsInformation);
